// =============================================================================
// hooks/crm/leadFilters.ts — Filtros da lista de leads <-> query string.
//
// Lógica PURA (sem React): é o que o teste cobre. A lista (`useLeads`) e os
// totais (`useLeadStats`) montam a query pela MESMA função — os cartões do CRM
// não podem contar um conjunto diferente do que a tabela mostra (CRM-05).
//
// Os validadores vêm de `LeadListFiltersSchema` (shared-schemas, F58-S07):
// um valor que o servidor recusaria com 400 (data inexistente, status
// desconhecido, busca acima de 120 caracteres) nunca sai do navegador.
// =============================================================================

import { LeadListFiltersSchema } from '@elemento/shared-schemas';

import type { LeadFilters } from './types';

const SHAPE = LeadListFiltersSchema.shape;

/** Teto da busca no contrato (`search` max 120). */
export const SEARCH_MAX_LENGTH = 120;

/** Filtros que a URL do CRM carrega (`agent_id` vem do chip "Meus leads"). */
export type LeadUrlFilters = Pick<
  LeadFilters,
  'search' | 'status' | 'no_agent' | 'stale' | 'created_from' | 'created_to'
> & { page: number };

/**
 * Lê os filtros da URL. Valor inválido é DESCARTADO em silêncio (link velho,
 * edição manual) — nunca vira 400 na lista. `created_from` posterior a
 * `created_to` derruba `created_to`, que o servidor também recusaria.
 */
export function parseLeadUrlFilters(params: URLSearchParams): LeadUrlFilters {
  const out: LeadUrlFilters = { page: 1 };

  const search = params.get('search')?.trim().slice(0, SEARCH_MAX_LENGTH);
  if (search) out.search = search;

  const status = SHAPE.status.safeParse(params.get('status') ?? undefined);
  if (status.success && status.data !== undefined) out.status = status.data;

  // Só `true` liga o filtro; `false` equivale a ausente (a URL fica limpa).
  const noAgent = SHAPE.no_agent.safeParse(params.get('no_agent') ?? undefined);
  if (noAgent.success && noAgent.data === true) out.no_agent = true;
  const stale = SHAPE.stale.safeParse(params.get('stale') ?? undefined);
  if (stale.success && stale.data === true) out.stale = true;

  const from = SHAPE.created_from.safeParse(params.get('created_from') ?? undefined);
  if (from.success && from.data !== undefined) out.created_from = from.data;
  const to = SHAPE.created_to.safeParse(params.get('created_to') ?? undefined);
  if (to.success && to.data !== undefined) out.created_to = to.data;
  if (out.created_from && out.created_to && out.created_from > out.created_to) {
    delete out.created_to;
  }

  const page = Number(params.get('page'));
  if (Number.isInteger(page) && page > 1) out.page = page;

  return out;
}

/**
 * Escreve os filtros numa URL já existente, preservando os parâmetros que não
 * são de filtro (`view`, `quick`). Filtro ausente remove a chave.
 */
export function writeLeadUrlFilters(
  base: URLSearchParams,
  filters: Partial<LeadUrlFilters>,
): URLSearchParams {
  const next = new URLSearchParams(base);
  const setOrDelete = (key: string, value: string | undefined): void => {
    if (value) next.set(key, value);
    else next.delete(key);
  };
  setOrDelete('search', filters.search);
  setOrDelete('status', filters.status);
  setOrDelete('no_agent', filters.no_agent === true ? 'true' : undefined);
  setOrDelete('stale', filters.stale === true ? 'true' : undefined);
  setOrDelete('created_from', filters.created_from);
  setOrDelete('created_to', filters.created_to);
  const page = filters.page ?? 1;
  setOrDelete('page', page > 1 ? String(page) : undefined);
  return next;
}

/**
 * Query string do `GET /api/leads` e do `GET /api/leads/stats`. Com
 * `includePaging: false` (totais) `page`/`limit` ficam de fora.
 *
 * `no_agent=true` NUNCA sai junto de `agent_id`: o servidor recusa o pedido
 * contraditório (400). O "sem agente" explícito vence o agente herdado de
 * "Meus leads".
 */
export function buildLeadFiltersQuery(
  filters: LeadFilters,
  options: { includePaging: boolean },
): string {
  const params = new URLSearchParams();
  if (options.includePaging) {
    if (filters.page !== undefined) params.set('page', String(filters.page));
    if (filters.limit !== undefined) params.set('limit', String(filters.limit));
  }
  if (filters.search) params.set('search', filters.search);
  if (filters.status) params.set('status', filters.status);
  if (filters.city_id) params.set('city_id', filters.city_id);
  if (filters.source) params.set('source', filters.source);
  if (filters.no_agent === true) params.set('no_agent', 'true');
  else if (filters.agent_id) params.set('agent_id', filters.agent_id);
  if (filters.stale === true) params.set('stale', 'true');
  if (filters.created_from) params.set('created_from', filters.created_from);
  if (filters.created_to) params.set('created_to', filters.created_to);
  return params.toString();
}

/** Filtros dos totais: os da lista, sem paginação. */
export function toStatsFilters(filters: LeadFilters): LeadFilters {
  const { page: _page, limit: _limit, ...rest } = filters;
  return rest;
}
